/**
 * Presentational component for a range filter field
 *
 * Renders a labeled pair of min/max text inputs with a dash separator
 * and a MultiSlider underneath. Spacing matches the Figma spec:
 * gap-4 (4px) between label and inputs, gap-12 between inputs and slider.
 * @module features/scouting/components/RangeFilterField/RangeFilterFieldView
 */

import { memo } from "react";

import MultiSlider from "@/components/organisms/MultiSlider";
import { Box } from "@/components/atoms/Box";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";
import { TextInput } from "@/components/atoms/TextInput";

/**
 * Theme-aware colors for range filter inputs.
 * Uses direct hex values because `dark:` NativeWind classes don't resolve
 * inside GlueStack Actionsheet/Modal portals on native iOS.
 */
type ThemeMode = "light" | "dark";

const RANGE_COLORS: Record<
  ThemeMode,
  {
    readonly label: string;
    readonly inputBg: string;
    readonly inputBorder: string;
    readonly inputText: string;
    readonly separator: string;
  }
> = {
  light: {
    label: "#8E8E8E",
    inputBg: "#FFFFFF",
    inputBorder: "#D3D3D3",
    inputText: "#737373",
    separator: "#A3A3A3",
  },
  dark: {
    label: "#A3A3A3",
    inputBg: "#1C1C1E",
    inputBorder: "#636366",
    inputText: "#D1D1D6",
    separator: "#8E8E93",
  },
};

/**
 * Resolves the color palette for a given dark mode flag.
 * @param isDark - Whether dark mode is active (defaults to false for backward compatibility)
 */
const resolveRangeColors = (isDark: boolean) =>
  RANGE_COLORS[isDark ? "dark" : "light"];

/**
 * Text color style.
 * design-system: dynamic-style waiver — data-driven value (theme-resolved color)
 * @param color - Resolved text color
 */
const rangeTextColorStyle = (color: string) => ({ color });

/**
 * Numeric input container theme-resolved chrome colors.
 * design-system: dynamic-style waiver — data-driven value (theme-resolved chrome).
 * Static geometry (radius/height/padding/border-width) is now expressed as
 * closed-token classNames; only the portal-safe theme colors remain runtime.
 * @param isDark - Whether dark mode is active
 */
const rangeInputChromeStyle = (isDark: boolean) => ({
  backgroundColor: resolveRangeColors(isDark).inputBg,
  borderColor: resolveRangeColors(isDark).inputBorder,
});

/** Props for RangeFilterFieldView */
interface RangeFilterFieldViewProps {
  /** Filter label displayed above the inputs */
  readonly label: string;
  /** Current minimum text value shown in the left input */
  readonly minText: string;
  /** Current maximum text value shown in the right input */
  readonly maxText: string;
  /** Called when the user types in the minimum input */
  readonly onMinTextChange: (text: string) => void;
  /** Called when the user types in the maximum input */
  readonly onMaxTextChange: (text: string) => void;
  /** Called when the minimum input loses focus — triggers clamping */
  readonly onMinBlur: () => void;
  /** Called when the maximum input loses focus — triggers clamping */
  readonly onMaxBlur: () => void;
  /** Current slider thumb positions as [min, max] */
  readonly sliderValue: number[];
  /** Lowest allowed slider value */
  readonly sliderMinValue: number;
  /** Highest allowed slider value */
  readonly sliderMaxValue: number;
  /** Slider step increment */
  readonly sliderStep: number;
  /** Called when the slider thumbs move */
  readonly onSliderChange: (value: [number, number]) => void;
  /** Whether values represent currency (forwarded to MultiSlider) */
  readonly isCurrency?: boolean;
  /** Whether to use logarithmic scale (forwarded to MultiSlider) */
  readonly useLogScale?: boolean;
  /** Whether dark mode is active — for portal-safe styling */
  readonly isDark?: boolean;
  /**
   * Optional handle on the whole field.
   *
   * The two inputs below carry the FIXED ids `range-filter-min-input` /
   * `range-filter-max-input`, which repeat once per range row — a section
   * holding more than one row (BASIC holds Age and Months Remaining;
   * PERFORMANCE holds four) therefore offers no way to address a specific
   * field. This id is what lets a caller name one. Optional so every existing
   * call site keeps working unchanged.
   */
  readonly testID?: string;
}

/**
 * Range filter field with text inputs and a dual-thumb slider
 * @param root0 - Component props
 * @param root0.testID - Optional handle on the field root
 * @param root0.label - Filter label
 * @param root0.minText - Min input text
 * @param root0.maxText - Max input text
 * @param root0.onMinTextChange - Min input change handler
 * @param root0.onMaxTextChange - Max input change handler
 * @param root0.onMinBlur - Min input blur handler
 * @param root0.onMaxBlur - Max input blur handler
 * @param root0.sliderValue - Current slider values
 * @param root0.sliderMinValue - Slider minimum
 * @param root0.sliderMaxValue - Slider maximum
 * @param root0.sliderStep - Slider step
 * @param root0.onSliderChange - Slider change handler
 * @param root0.isCurrency - Currency formatting flag
 * @param root0.useLogScale - Log scale flag
 * @param root0.isDark - Whether dark mode is active
 */
const RangeFilterFieldView = ({
  label,
  minText,
  maxText,
  onMinTextChange,
  onMaxTextChange,
  onMinBlur,
  onMaxBlur,
  sliderValue,
  sliderMinValue,
  sliderMaxValue,
  sliderStep,
  onSliderChange,
  isCurrency,
  useLogScale,
  isDark = false,
  testID,
}: RangeFilterFieldViewProps) => (
  <Stack className="gap-1" testID={testID}>
    <Text
      variant="unstyled"
      className="text-title-sm"
      // eslint-disable-next-line no-restricted-syntax -- data-driven portal-safe theme color; dark: classes don't resolve inside GlueStack portals on native, no closed token binding
      UNSAFE_style={rangeTextColorStyle(resolveRangeColors(isDark).label)}
    >
      {label}
    </Text>
    <Stack className="gap-3">
      <Stack direction="horizontal" className="items-center gap-4">
        <Box
          className="h-11 flex-1 flex-row items-center rounded-sm border px-3"
          // eslint-disable-next-line no-restricted-syntax -- data-driven portal-safe theme bg/border colors; dark: classes don't resolve inside GlueStack portals on native, no closed token binding
          UNSAFE_style={rangeInputChromeStyle(isDark)}
        >
          <TextInput
            // `min-w-0` is the containment half of SE-7731 — a `flex-1`
            // `<input>` cannot shrink below its UA min-content width without
            // it, and this row puts two of them side by side. Not the
            // ellipsis half: these are numeric fields whose content is short.
            className="min-w-0 flex-1 p-0 text-body"
            // eslint-disable-next-line no-restricted-syntax -- web outline reset (no token) + data-driven portal-safe theme text color, no closed token binding
            UNSAFE_style={
              {
                outlineStyle: "none",
                color: resolveRangeColors(isDark).inputText,
              } as Record<string, string | number>
            }
            value={minText}
            onChangeText={onMinTextChange}
            onBlur={onMinBlur}
            keyboardType="numeric"
            testID="range-filter-min-input"
          />
        </Box>
        <Box className="w-2 items-center justify-center self-stretch">
          <Text
            variant="unstyled"
            className="text-title-sm"
            // eslint-disable-next-line no-restricted-syntax -- data-driven portal-safe theme color; dark: classes don't resolve inside GlueStack portals on native, no closed token binding
            UNSAFE_style={rangeTextColorStyle(
              resolveRangeColors(isDark).separator
            )}
          >
            -
          </Text>
        </Box>
        <Box
          className="h-11 flex-1 flex-row items-center rounded-sm border px-3"
          // eslint-disable-next-line no-restricted-syntax -- data-driven portal-safe theme bg/border colors; dark: classes don't resolve inside GlueStack portals on native, no closed token binding
          UNSAFE_style={rangeInputChromeStyle(isDark)}
        >
          <TextInput
            // `min-w-0` is the containment half of SE-7731 — a `flex-1`
            // `<input>` cannot shrink below its UA min-content width without
            // it, and this row puts two of them side by side. Not the
            // ellipsis half: these are numeric fields whose content is short.
            className="min-w-0 flex-1 p-0 text-body"
            // eslint-disable-next-line no-restricted-syntax -- web outline reset (no token) + data-driven portal-safe theme text color, no closed token binding
            UNSAFE_style={
              {
                outlineStyle: "none",
                color: resolveRangeColors(isDark).inputText,
              } as Record<string, string | number>
            }
            value={maxText}
            onChangeText={onMaxTextChange}
            onBlur={onMaxBlur}
            keyboardType="numeric"
            testID="range-filter-max-input"
          />
        </Box>
      </Stack>
      <Box className="px-2">
        <MultiSlider
          value={sliderValue}
          minValue={sliderMinValue}
          maxValue={sliderMaxValue}
          step={sliderStep}
          onChange={onSliderChange}
          isCurrency={isCurrency}
          useLogScale={useLogScale}
          hideLabels
        />
      </Box>
    </Stack>
  </Stack>
);

RangeFilterFieldView.displayName = "RangeFilterFieldView";

export default memo(RangeFilterFieldView);
